<template lang="pug">
div(:class="{ '-uploading': pendingUpload }" ref="wrapper")
  form.dz(:class="{ 'dz--show': savedURL }" ref="dropzoneTarget")
    .dz__content.border.border-4.border-dashed.min-h-full.text-sky-600.rounded-xl.p-8.bg-sky-100.text-center.flex.flex-col.gap-4.items-center.justify-center.primary-sky
      //- input.dz__input(@click.stop :name="uuid"  type="file")
      .dz__preview.text-7xl(v-if="!imgSrc")
        uc-icon(icon="mdi:cloud-upload-outline")

      .-center(v-if="imgSrc")
        p.font-bold
          | Please check that your logo looks OK against a light background.
        p.italic
          | If not, you can drag in a new image.

      .flex.flex-col.sm_flex-row.gap-10(v-if="imgSrc")
        mixin logo-container
          .p-5.rounded-xl.flex.items-center.h-auto&attributes(attributes)
            img.dz__img.upload-logo-preview(
              :src="imgSrc"
              class="min-w-[10em] max-w-[20em] max-h-[10em]"
            )

        //- logo previews
        +logo-container.bg-white
        //- +logo-container.bg-black

      .prose-sm.text-sky-600
        p.dz__message.font-bold.text-xl
          span(v-if="pendingUpload") Saving logo... {{ percentUploaded }}
          span(v-else-if="savedURL") Logo saved.
          span(v-else-if="uploadError") We had a problem, please try again later.
          span(v-else="") Drag an image here

        p.-center(v-if="!savedURL")
          | You may upload a PNG, JPG, SVG, or GIF up to 1 megabyte in size.
          br
          | If you need help, please be in touch with our support team.
      .dz__border
        button.p-2.btn-primary.primary-red.dz__delete.font-bold(
          @click="removeImage"
          v-if="savedURL"
        )
          uc-icon.me-2.text-lg(icon="material-symbols:delete-forever")
          span Delete logo
  //- span v-show="pendingUpload" Uploading... {{ progress > 0 ? progress + '%' : '' }}
</template>

<script lang="ts">
import Dropzone from 'dropzone';
import 'dropzone/dist/dropzone.css';
import SCORM, { set } from '../lib/SCORM';
import { closeLightbox } from '../lib/openLightbox';
import axios from 'axios';
import useContent from '../stores/useContent';
import getEditorWindow from '../lib/getEditorWindow';

export default {
  setup() {
    const content = useContent();
    return { content };
  },
  data() {
    return {
      pendingUpload: false,
      logoPreview: '',
      uploadError: false,
      progress: 0,
      dz: null,
      uuid: 'files' + Math.random().toString().split('.')[1]
    };
  },
  computed: {
    savedURL() {
      return this.content.compiled.course['logo-light'];
    },

    percentUploaded() {
      const percent = Math.ceil(this.progress * 100);

      return percent > 0 && percent < 100 ? `(${percent})%` : '';
    },

    imgSrc() {
      return this.logoPreview || this.savedURL || false;
    }
  },
  mounted() {
    if (!this.$refs.dropzoneTarget) return;

    // we need to change the default logo value of true to '' so it's not used as a URL
    if (this.savedURL === true) this.setLogo('');

    this.dz = new Dropzone(this.$refs.dropzoneTarget, {
      url: 'https://lms.vinciworks.com/SharedScormCourses2012/CustomScormCourses/Internal/165/upload/upload.php',
      autoProcessQueue: false,
      addedfile: file => {
        var reader = new FileReader();
        reader.onload = e => {
          var dataURL = e.target.result;
          this.uploadImage(dataURL, file);
        };

        reader.readAsDataURL(file);
      }
    });

    this.dz.on('addedfile', file => {
      // console.log('Logo added', { file });
    });
  },
  methods: {
    closeLightbox,

    setLogo(url: string) {
      // now defaults to use logo on white bg
      const path = '$course.logo-light';

      // Resolve the authoring host lazily (see getEditorWindow) — its presence is
      // the authoring gate, resolved at save time so it sees the host's
      // runtime-assigned `acceptPathFromPreview` handler.
      const hostWindow = getEditorWindow('acceptPathFromPreview');
      if (hostWindow?.acceptPathFromPreview) {
        // Authoring: update the local store synchronously so the preview reflects
        // the new logo immediately, then persist the shorthand path into the
        // Monaco YAML draft — the draft's source of truth. The default path
        // (setPath -> commit -> updateCustomClientAPI) only saves to the draft
        // when clientEditMode is set, which it isn't in authoring.
        this.content.setEditValue('patches', {
          ...this.content.edits?.patches,
          [path]: [{ value: url }]
        });
        hostWindow.acceptPathFromPreview(path, url);
        // Logo upload is a discrete action, so commit the draft immediately
        // instead of waiting for the 30s background debounce. Mirror the
        // uc-tiptap-author blur path: defer so acceptPathFromPreview -> Monaco
        // onChange -> save assigns latestSavePromise before flushInlineCommit's
        // waitForLatestSave runs, otherwise the commit captures a stale draft.
        setTimeout(() => {
          getEditorWindow('flushInlineCommit')?.flushInlineCommit?.();
        }, 100);
      } else {
        this.content.setPath(path, url);
      }
    },

    async uploadImage(dataURL, file) {
      this.logoPreview = dataURL;
      this.pendingUpload = true;
      this.uploadError = false;
      this.progress = 0;

      try {
        const url = await this.upload(file);
        this.setLogo(url);
      } catch (e) {
        this.uploadError = true;
        console.error(e);
      }

      this.pendingUpload = false;
      this.progress = 0;
    },

    async removeImage() {
      const confirm = await window.confirm(
        'Are you sure you want to remove this logo?'
      );

      if (confirm) {
        this.logoPreview = '';
        this.setLogo('');
      }
    },

    async upload(file) {
      let formData = new FormData();
      formData.append('file', file);
      formData.append('username', SCORM.userName);

      return new Promise(resolve => {
        axios
          .post(
            'https://lms.vinciworks.com/SharedScormCourses2012/CustomScormCourses/Internal/165/upload/upload.php',
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            this.uploadError = true;
          });
      }).catch(e => {
        this.uploadError = true;
      });
    }
  }
};
</script>

<style>
#lightbox-upload-logo {
  .lightbox {
    @apply p-4;

    /* hide the light box buttons (OK/Continue/Next etc.) */
    .wrapper > .flex > .self-end {
      display: none;
    }
  }
}
</style>
